import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import axios from 'axios'
import { API_CONFIG } from '../../config/apiConfig'
import { showToast } from '../../utils/messageUtil';
import { useUser } from '../../contexts/UserContext'
import './index.css'

// 缓存管理函数已从 auth_access_util.js 导入

export default function DelayApproval() {

    const navigate = useNavigate()
    const { userInfo, loading: userLoading, refreshUserInfo } = useUser()
    const [hasRefreshed, setHasRefreshed] = useState(false)

    // 检查用户信息是否完整
    const isUserInfoComplete = () => {
        if (!userInfo) return false
        const hasUserId = userInfo.user_id || userInfo.open_id
        // 延期审批页面只需要user_id
        return hasUserId !== ''
    }

    // 如果用户信息缺失，重新获取
    useEffect(() => {
        // 只有在加载完成后、用户信息不完整且还未刷新过时才重新获取
        if (!userLoading && !isUserInfoComplete() && !hasRefreshed) {
            console.log('延期审批页面：用户信息不完整，重新获取...')
            setHasRefreshed(true)
            refreshUserInfo()
        }
    }, [userLoading, userInfo, refreshUserInfo, hasRefreshed])

    const [approvals, setApprovals] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [approvalModalVisible, setApprovalModalVisible] = useState(false)
    const [selectedApproval, setSelectedApproval] = useState(null)
    const [approvalOpinion, setApprovalOpinion] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [newEndTime, setNewEndTime] = useState('')

    const containerRef = useRef(null)
    const debounceTimerRef = useRef(null)
    const latestKeywordRef = useRef('')

    // 筛选条件
    const [filters, setFilters] = useState({
        keyword: ''
    })

    // 处理筛选条件变化，自动查询
    const handleFilterChange = (e) => {
        const { name, value } = e.target
        setFilters(prev => ({
            ...prev,
            [name]: value
        }))
        latestKeywordRef.current = value
        
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }
        
        debounceTimerRef.current = setTimeout(() => {
            fetchApprovals(1)
        }, 500)
    }

    const fetchApprovals = useCallback(async (page = 1) => {
        try {
            if (page === 1) {
                setLoading(true)
            } else {
                setLoadingMore(true)
            }
            setError(null)

            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/delayReturnCar/getCarDelayReturnList`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                page: page,
                rows: 10,
                keyword: latestKeywordRef.current
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.success) {
                const approvalData = response.data.result.rows || []
                const total = response.data.result.total || 0
                const records = response.data.result.records || 0
                const rowsPerPage = 10
                const calculatedTotalPages = Math.ceil(records / rowsPerPage)

                setTotalPages(calculatedTotalPages)
                setCurrentPage(page)
                setHasMore(page < calculatedTotalPages)

                if (page === 1) {
                    setApprovals(approvalData)
                } else {
                    setApprovals(prevApprovals => [...prevApprovals, ...approvalData])
                }
            } else {
                if (page === 1) {
                    setApprovals([])
                }
                setError('获取审批数据失败')
            }
        } catch (error) {
            console.error('获取审批数据失败:', error)
            if (page === 1) {
                setApprovals([])
            }
            setError('网络错误，请稍后重试')
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }, [])

    useEffect(() => {
        fetchApprovals(1)
    }, [fetchApprovals])

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    // 处理审批
    const handleApproval = (approval) => {
        setSelectedApproval(approval)
        setApprovalOpinion('')
        // 将后端格式转换为 datetime-local 格式
        const backendTime = approval.newEndTime || ''
        const localTime = backendTime ? backendTime.replace(' ', 'T') : ''
        setNewEndTime(localTime)
        setApprovalModalVisible(true)
    }

    // 将 datetime-local 格式转换为后端格式
    const formatToBackend = (localDateTime) => {
        if (!localDateTime) return ''
        return localDateTime.replace('T', ' ') + ':00'
    }

    // 提交审批
    const submitApproval = async (approvalStatus) => {
        if (!selectedApproval) return

        // 驳回时审批意见必填
        if (approvalStatus === '2' && !approvalOpinion.trim()) {
            showToast('驳回时审批意见必填', 'warning');
            return
        }

        // 验证延期时间不能早于到期时间
        if (approvalStatus === '1' && newEndTime) {
            const expectedEnd = new Date(selectedApproval.oldEndTime)
            const newEnd = new Date(newEndTime)
            if (newEnd <= expectedEnd) {
                showToast('延期时间不能早于或等于到期时间', 'warning');
                return
            }
        }

        try {
            setSubmitting(true)

            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/delayReturnCar/approveDelayReturnApplication`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                applicationId: selectedApproval.applicationId,
                id: selectedApproval.id,
                newEndTime: formatToBackend(newEndTime),
                approvalStatus: approvalStatus,
                approvalOpinion: approvalOpinion || (approvalStatus === '1' ? '同意' : '驳回')
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.success) {
                showToast('审批成功', 'success');
                setApprovalModalVisible(false)
                fetchApprovals(1)
            } else {
                showToast('审批失败：' + (response.data?.message || '未知错误'), 'error');
            }
        } catch (error) {
            console.error('审批失败:', error)
            showToast('审批失败，请稍后重试', 'error');
        } finally {
            setSubmitting(false)
        }
    }

    // 关闭弹窗
    const handleCloseModal = () => {
        setApprovalModalVisible(false)
        setSelectedApproval(null)
        setNewEndTime('')
    }

    // 返回首页
    const handleBack = () => {
        navigate('/')
    }

    return (
        <div
            className="delay-approval-page"
            ref={containerRef}
        >
            <h2>延期审批</h2>

            {/* 筛选条件 */}
            <div className="filter-container">
                    <div className="filter-item search-input">
                        <input
                            type="text"
                            name="keyword"
                            value={filters.keyword}
                            onChange={handleFilterChange}
                            placeholder="车牌号或申请单号"
                        />
                    </div>
            </div>

            {loading ? (
                <div className="loading-indicator">
                    <p>正在加载审批数据...</p>
                </div>
            ) : error ? (
                <div className="error-message">
                    <p>{error}</p>
                    <button className="retry-btn" onClick={() => fetchApprovals(1)}>重试</button>
                </div>
            ) : (
                <div className="approvals-list">
                    {approvals.length === 0 ? (
                        <div className="no-approvals">
                            <p>暂无延期申请</p>
                        </div>
                    ) : (
                        <>
                            <div className="approval-cards">
                                {approvals.map(approval => (
                                    <div key={approval.id} className="approval-card">
                                        <div className="card-header">
                                            <h3>{approval.plateNumber}</h3>
                                            <span className="application-code">{approval.applicationNo}</span>
                                        </div>
                                        <div className="card-status" style={{
                                            padding: '8px 15px',
                                            backgroundColor: approval.approvalStatus === 0 ? '#fff3cd' : approval.approvalStatus === 1 ? '#d4edda' : approval.approvalStatus === 2 ? '#f8d7da' : '#f0f0f0',
                                            color: approval.approvalStatus === 0 ? '#856404' : approval.approvalStatus === 1 ? '#155724' : approval.approvalStatus === 2 ? '#721c24' : '#333',
                                            textAlign: 'center',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}>
                                            {approval.approvalStatus === 0 ? '待审批' :
                                                approval.approvalStatus === 1 ? '审批通过' :
                                                    approval.approvalStatus === 2 ? '审批拒绝' :
                                                        approval.approvalStatus}
                                        </div>
                                        <div className="card-body">
                                            <div className="card-info">
                                                <div className="info-item">
                                                    <span className="label">申请人:</span>
                                                    <span className="value">{approval.applicantName}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">申请人电话:</span>
                                                    <span className="value">{approval.applicantPhone}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">所属部门:</span>
                                                    <span className="value">{approval.applicantDept}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">驾驶方式:</span>
                                                    <span className="value">{approval.driveType === 1 ? '自驾' : '司机代驾'}</span>
                                                </div>
                                                {approval.driveType === 2 && (
                                                    <div className="info-item">
                                                        <span className="label">代驾司机:</span>
                                                        <span className="value">{approval.driverName}</span>
                                                    </div>
                                                )}
                                                <div className="info-item">
                                                    <span className="label">申请类型:</span>
                                                    <span className="value">
                                                        {approval.isPersonal === 0 ? '本人申请' : '代人申请'}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">用车人:</span>
                                                    <span className="value">{approval.vehicleUserName}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">使用场景:</span>
                                                    <span className="value">
                                                        {approval.usageScenarios === 1 ? '员工外出' :
                                                            approval.usageScenarios === 2 ? '一般客户接待' :
                                                                approval.usageScenarios === 3 ? '重要客户接待' :
                                                                    approval.usageScenarios}
                                                    </span>
                                                </div>
                                                <div className="info-item full-width">
                                                    <span className="label">用车时间:</span>
                                                    <span className="value">{approval.expectedStartTime} 至 {approval.oldEndTime}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">出发地:</span>
                                                    <span className="value">{approval.expectedLocation}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">目的地:</span>
                                                    <span className="value">{approval.destination}</span>
                                                </div>
                                                {approval.passengers && approval.passengers.trim() !== '' && (
                                                    <div className="info-item">
                                                        <span className="label">同行人:</span>
                                                        <span className="value">{approval.passengers}</span>
                                                    </div>
                                                )}
                                                <div className="info-item full-width">
                                                    <span className="label">用车原因:</span>
                                                    <span className="value">{approval.reason}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">到期时间:</span>
                                                    <span className="value">{approval.oldEndTime}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="label">延期时间:</span>
                                                    <span className="value">{approval.newEndTime}</span>
                                                </div>
                                                <div className="info-item full-width">
                                                    <span className="label">延期理由:</span>
                                                    <span className="value">{approval.delayReason}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card-footer">
                                            {approval.approvalStatus === 0 && (
                                                <button
                                                    className="approve-btn"
                                                    onClick={() => handleApproval(approval)}
                                                    style={{
                                                        padding: '12px 20px',
                                                        backgroundColor: '#1890ff',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '16px',
                                                        fontWeight: 'bold',
                                                        height: '40px'
                                                    }}
                                                >
                                                    延期审批
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {loadingMore ? (
                                <div className="loading-more">
                                    <p>加载中...</p>
                                </div>
                            ) : hasMore ? (
                                <div className="load-more-container">
                                    <button className="load-more-btn" onClick={() => fetchApprovals(currentPage + 1)}>
                                        加载更多
                                    </button>
                                </div>
                            ) : (
                                <div className="no-more-data">
                                    <p>没有更多数据了</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            <div className="action-buttons">
                <button className="back-btn" onClick={handleBack}>返回首页</button>
            </div>

            {/* 审批弹窗 */}
            {approvalModalVisible && selectedApproval && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '30px',
                        width: '90%',
                        maxWidth: '600px',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{
                            margin: '0 0 20px 0',
                            textAlign: 'center',
                            fontSize: '18px',
                            color: '#333'
                        }}>
                            延期审批
                        </h3>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '20px',
                            marginBottom: '30px'
                        }}>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>车牌号:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.plateNumber}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>申请单号:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.applicationNo}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>开始时间:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.expectedStartTime}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>到期时间:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.oldEndTime}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                gridColumn: '1 / -1'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#d9534f',
                                    marginBottom: '5px'
                                }}>延期时间:</span>
                                <input
                                    type="datetime-local"
                                    value={newEndTime}
                                    onChange={(e) => setNewEndTime(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        border: '2px solid #d9534f',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                gridColumn: '1 / -1'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>延期理由:</span>
                                <span style={{
                                    color: '#333'
                                }}>{selectedApproval.delayReason}</span>
                            </div>
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                gridColumn: '1 / -1'
                            }}>
                                <span style={{
                                    fontWeight: 'bold',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>审批意见:</span>
                                <textarea
                                    value={approvalOpinion}
                                    onChange={(e) => setApprovalOpinion(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        resize: 'vertical',
                                        minHeight: '80px'
                                    }}
                                    placeholder="请输入审批意见"
                                />
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '10px',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={() => submitApproval('1')}
                                disabled={submitting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    flex: 1,
                                    minWidth: '80px'
                                }}
                            >
                                {submitting ? '处理中...' : '通过'}
                            </button>
                            <button
                                onClick={() => submitApproval('2')}
                                disabled={submitting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    flex: 1,
                                    minWidth: '80px'
                                }}
                            >
                                {submitting ? '处理中...' : '驳回'}
                            </button>
                            <button
                                onClick={handleCloseModal}
                                disabled={submitting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    flex: 1,
                                    minWidth: '80px'
                                }}
                            >
                                取消
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}